
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Copy, AlertTriangle, ArrowRight, RotateCcw } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import StepCard from "../StepCard";
import { useNavigate } from "react-router-dom";
import { CALIBRATION_KEYS, CalibrationMetadata } from "@/constants/calibrationKeys";

interface ResultsViewProps {
  selectedSlicer: "creality" | "orca" | "anycubic";
  selectedMethod: "classic" | "yolo";
  initialFlowRatio: string;
  modifier: string;
  newFlowRatio: string;
  onPrevious: () => void;
  onStartOver: () => void;
  currentPass: number;
  onNextPass: () => void;
}

const ResultsView = ({
  selectedSlicer,
  selectedMethod,
  initialFlowRatio,
  modifier,
  newFlowRatio,
  onPrevious,
  onStartOver,
  currentPass,
  onNextPass
}: ResultsViewProps) => {
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Normalize decimal separators to standard decimal point
  const normalizeDecimalSeparator = (value: string) => {
    // Replace comma, apostrophe, and Arabic decimal separator with decimal point
    return value.replace(/[,''٫]/g, '.');
  };

  // Detect the decimal separator used in the input and format the result accordingly
  const formatWithOriginalSeparator = (value: string, originalInput: string) => {
    if (!value) return value;
    
    // Find which separator was used in the original input
    let usedSeparator = '.';
    if (originalInput.includes(',')) usedSeparator = ',';
    else if (originalInput.includes("'")) usedSeparator = "'";
    else if (originalInput.includes('٫')) usedSeparator = '٫';
    
    // If the original used a different separator, replace the decimal point
    if (usedSeparator !== '.') {
      return value.replace('.', usedSeparator);
    }
    
    return value;
  };

  const getFlowRatioStatus = () => {
    if (!newFlowRatio) return "neutral";
    const normalizedValue = normalizeDecimalSeparator(newFlowRatio);
    const value = parseFloat(normalizedValue);
    if (value > 1.2 || value < 0.8) return "warning";
    return "good";
  };

  const getExtrusionType = () => {
    if (!newFlowRatio) return "";
    const normalizedValue = normalizeDecimalSeparator(newFlowRatio);
    const value = parseFloat(normalizedValue);
    if (value > 1.2) return "overextrusion";
    if (value < 0.8) return "underextrusion";
    return "";
  };

  const getWarningMessage = () => {
    const extrusionType = getExtrusionType();
    let message = "Warning: This value is outside the typical recommended range.";
    if (extrusionType === "overextrusion") {
      message += " This is likely to cause overextrusion.";
    } else if (extrusionType === "underextrusion") {
      message += " This may cause underextrusion.";
    }
    return message;
  };

  const getBlueTipText = () => {
    if (selectedSlicer === "orca" && currentPass === 2) {
      return "• Update your filament profile with the new flow ratio and save it.";
    }
    return (
      <>
        <li>• Update your filament profile with the new flow ratio and save it.</li>
        <li>• If you want to fine-tune the flow ratio, perform a second pass.</li>
      </>
    );
  };

  const copyToClipboard = () => {
    if (newFlowRatio) {
      const formattedValue = formatFlowRatio(newFlowRatio);
      navigator.clipboard.writeText(formattedValue);
      toast({
        title: "Copied!",
        description: `New flow ratio ${formattedValue} copied to clipboard`,
      });
    }
  };

  // Format the flow ratio to remove trailing zeros and preserve original separator
  const formatFlowRatio = (value: string) => {
    const normalizedValue = normalizeDecimalSeparator(value);
    const num = parseFloat(normalizedValue);
    if (isNaN(num)) return value;
    
    const formattedNum = num.toString();
    return formatWithOriginalSeparator(formattedNum, initialFlowRatio);
  };

  const getFormulaText = () => {
    if (selectedMethod === "classic") {
      return `${initialFlowRatio || "Initial"} × (100 + ${modifier || "Modifier"}) ÷ 100 = ${formatFlowRatio(newFlowRatio) || "Result"}`;
    } else {
      return `${initialFlowRatio || "Initial"} + (${modifier || "Modifier"}) = ${formatFlowRatio(newFlowRatio) || "Result"}`;
    }
  };

  const handleFinish = () => {
    // Mark calibration as completed and navigate to tools
    const calibrationData: CalibrationMetadata = {
      completed: true,
      completedAt: new Date().toISOString(),
      slicerUsed: selectedSlicer
    };
    localStorage.setItem(CALIBRATION_KEYS.flow, JSON.stringify(calibrationData));
    
    toast({
      title: "Calibration Complete",
      description: "Calibration complete and settings updated.",
    });
    
    navigate('/tools');
  };

  // Custom button for the right side
  const getRightButton = () => {
    if (currentPass === 1) {
      return (
        <Button 
          onClick={onNextPass} 
          variant="outline" 
          className="min-h-[44px] px-6 border-gray-300 dark:border-gray-600"
        >
          Pass 2
        </Button>
      );
    } else {
      return (
        <Button 
          onClick={handleFinish}
          variant="outline" 
          className="min-h-[44px] px-6 border-gray-300 dark:border-gray-600"
        >
          Done
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      );
    }
  };

  return (
    <StepCard
      title=""
      onPrevious={onPrevious}
      showPrevious
      isLastStep
      customRightButton={getRightButton()}
    >
      <div className="space-y-4">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
          <h3 className="text-lg font-semibold text-left text-gray-900 dark:text-white">Your New Flow Ratio</h3>
        </div>

        <div className={`p-6 rounded-lg border-2 ${
          getFlowRatioStatus() === "warning" 
            ? "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800" 
            : "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"
        }`}>
          <div className="flex flex-col items-center gap-4 text-center">
            <div>
              <Label className="text-sm font-medium">New Flow Ratio</Label>
              <div className={`text-3xl font-bold ${
                getFlowRatioStatus() === "warning" 
                  ? "text-red-600 dark:text-red-400" 
                  : "text-green-600 dark:text-green-400"
              }`}>
                {formatFlowRatio(newFlowRatio)}
              </div>
              {getFlowRatioStatus() === "warning" && (
                <div className="flex items-center justify-center gap-1 mt-2 text-sm text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-4 w-4" />
                  {getWarningMessage()}
                </div>
              )}
            </div>
            <Button
              onClick={copyToClipboard}
              variant="outline"
              size="lg"
              className="min-h-[44px] border-gray-300 dark:border-gray-600"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            {getBlueTipText()}
          </ul>
        </div>
        
        <Collapsible open={summaryExpanded} onOpenChange={setSummaryExpanded}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-muted/50 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-muted/70 transition-colors">
            <h4 className="font-semibold">Summary & Calculation</h4>
            <ChevronDown className={`h-4 w-4 transition-transform ${summaryExpanded ? 'transform rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="p-4 bg-muted/50 rounded-lg border border-gray-300 dark:border-gray-600 space-y-4">
              {/* Calculation Breakdown */}
              <div className="space-y-2">
                <h5 className="font-medium text-sm">Calculation Breakdown</h5>
                <p className="font-mono text-sm bg-background p-2 rounded border">
                  {getFormulaText()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedMethod === "classic" 
                    ? "Classic method uses percentage-based adjustments"
                    : "YOLO method uses direct decimal adjustments for faster convergence"
                  }
                </p>
              </div>
              
              {/* Summary Details */}
              <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                <div>
                  <span className="text-muted-foreground">Slicer:</span>
                  <div className="font-mono font-bold">{selectedSlicer === "creality" ? "Creality Print" : selectedSlicer === "orca" ? "Orca Slicer" : "Anycubic Next"}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Initial Flow Ratio:</span>
                  <div className="font-mono font-bold">{initialFlowRatio}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Modifier:</span>
                  <div className="font-mono font-bold">{parseFloat(modifier) > 0 ? '+' : ''}{modifier}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">New Flow Ratio:</span>
                  <div className={`font-mono font-bold text-lg ${
                    getFlowRatioStatus() === "warning" 
                      ? "text-red-600 dark:text-red-400" 
                      : "text-green-600 dark:text-green-400"
                  }`}>
                    {formatFlowRatio(newFlowRatio)}
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </StepCard>
  );
};

export default ResultsView;
